import type { SavedPaymentMethod } from '@/classes/stripe';
import dayjs from 'dayjs';

/**
 * Helpers for the Billing "Other saved methods" list (CNV-4407).
 *
 * These are pure functions so they can be unit-tested in isolation and reused
 * by the Vuex `subscription` getter and the list components. They intentionally
 * do not perform any I/O.
 *
 * Same-fingerprint duplicates are left intact: the list API returns them so
 * the owner can delete redundant Stripe payment-method records. Ordering uses
 * the card's creation date (`created_at`) because Stripe does not expose a
 * "last used" timestamp.
 */

function createdAtTime(method: SavedPaymentMethod): number {
  if (!method.created_at) {
    return 0;
  }
  const time = dayjs(method.created_at).valueOf();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Sorts methods by creation date, most recent first. Methods without a creation
 * date are treated as oldest. Does not mutate the input array.
 */
export function sortByCreatedAtDesc(methods: SavedPaymentMethod[]): SavedPaymentMethod[] {
  return [...methods].sort((a, b) => createdAtTime(b) - createdAtTime(a));
}

/**
 * Returns the current default saved payment method, if any.
 */
export function getDefaultPaymentMethod(methods: SavedPaymentMethod[]): SavedPaymentMethod | null {
  return methods.find((method) => method.is_default) ?? null;
}

/**
 * Builds the "Other saved methods" list: excluding the default method, sorted
 * by creation date descending. Same-fingerprint duplicates are kept so they
 * remain removable.
 */
export function getOtherSavedMethods(methods: SavedPaymentMethod[]): SavedPaymentMethod[] {
  const others = methods.filter((method) => !method.is_default);
  return sortByCreatedAtDesc(others);
}
